import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ArtifactLibraryPanel from './ArtifactLibraryPanel';
import DeckCollabPanel from './DeckCollabPanel';
import DeckInsightsPanel from './DeckInsightsPanel';
import ArtifactViewer, { type ViewerTarget } from './ArtifactViewer';
import { useSlideViewTracker } from '../hooks/useSlideViewTracker';
import { ArrowsOut, Stack, Monitor, Sparkle, ShareNetwork, X, DownloadSimple, Notebook, PencilSimple, FloppyDisk, Graph, FilePdf, UploadSimple, Palette, Users, ChatCircle } from '@phosphor-icons/react';
import ShareDialog from './ShareDialog';
import { ImportDialog, BrandDialog } from './ImportBrandDialogs';
import MembersDialog from './MembersDialog';
import { uploadContextFile } from '../api/client';
// BuildModePicker + setDeckMode removed in generic-viewer refactor
// (PP main #87 alignment, Stage 4). The agent picks build mode per
// turn from user intent; no deck-level pre-pick.
import ConceptCloud from './ConceptCloud';
import { PanelState } from '../uikit';
import type { GatherAudience, GatherGoal } from './gather/GatherCards';
import MarkdownPlanEditor from './plan/MarkdownPlanEditor';
import StoryboardPanel, { type StoryCard } from './plan/StoryboardPanel';
import { parseStoryboard, serializeStoryboard } from '../utils/storyboardData';
import type { Phase } from '../types';
import { notifyError, notifyInfo } from '../hooks/useNotify';
import { apiFetch, AuthExpiredError } from '../api/auth-fetch';
import { getVersion, saveSlide, exportPptxUrl, type SlideRow } from '../api/client';
import { marked } from 'marked';

type TabKey = 'brief' | 'storyboard' | 'notes' | 'presentation' | 'artifacts' | 'insights';

// Browser-side export (ported from pitchpilot-cf): PPTX via dom-to-pptx
// with screenshot fallback, PDF via html2canvas+jsPDF, progress overlay.
type ExportKind = 'pptx' | 'pdf';
interface ExportProgressState {
  kind: ExportKind;
  label: string;
  progress: number;
  status: 'running' | 'complete' | 'error';
}

interface PreviewPanelProps {
  phase: Phase;
  /** Viewer-only surfaces can omit project mutation/sharing utilities. */
  presentationReadOnly?: boolean;
  deckId: string | null;
  /** T1.3 — stage a cross-deck slide from the artifact viewer as a
   *  pending composer reference. Provided by App (owns slideRefs). */
  onReferenceSlide?: (r: { deck_id: string; version: string; slide_num: number; deck_title: string }) => void;
  /** Workspace the deck lives in; used by child dialogs that need the
   *  workspace-scoped tulpa registry (MembersDialog → /tulpas?ws=…). */
  workspaceId?: string | null;
  deckTitle?: string;
  /** Build mode set on the deck. null = not yet picked (legacy or fresh) */
  deckMode?: 'html' | 'pptx' | null;
  /** PPTX theme name when mode === 'pptx' */
  deckTheme?: 'dify-default' | 'dify-dark' | null;
  /** Refetch the deck after BuildModePicker submits so deckMode/Theme update */
  onDeckChanged?: () => void;
  isGenerating: boolean;
  currentActivity: string;
  onAdvancePhase?: (phase: Phase) => void;
  planMarkdown: string;
  onPlanMarkdownChange: (md: string) => void;
  audienceInfo: GatherAudience | null;
  goalInfo: GatherGoal | null;
  keyMessages: string[] | null;
  maxPhaseReached?: Phase;
  previewRefreshKey?: number;
  contextVersion?: number;
  /** Parent-prefetched context items, threaded to ConceptCloud so it
   *  doesn't re-fetch listContext on mount. Eliminates a ~2.4s
   *  duplicate on the deck-open hot path. */
  contextItems?: { key: string; content: string }[];
  /** Deck id for which contextItems reflects a completed parent fetch.
   *  Lets ConceptCloud distinguish "parent still loading" from
   *  "parent loaded; this deck genuinely has no context". */
  contextHydratedDeckId?: string | null;
  planTask?: 'create' | 'update' | null;
  previewTask?: 'create' | 'update' | null;
  notesTask?: 'create' | 'update' | null;
  planSaveStatus?: 'idle' | 'saving' | 'saved';
  planUpdatedAt?: string | number;
  planVersions?: { key: string; label: string }[];
  activePlanKey?: string;
  onSelectPlanVersion?: (key: string) => void;
  onPlanSave?: (md: string) => void;
  notesMarkdown: string;
  onNotesChange: (md: string) => void;
  onNotesSave?: (md: string) => void;
  notesSaveStatus?: 'idle' | 'saving' | 'saved';
  notesUpdatedAt?: string | number;
  notesVersions?: { key: string; label: string }[];
  activeNotesKey?: string;
  onSelectNotesVersion?: (key: string) => void;
  previewGeneratingNoHTML?: boolean;
  artifacts?: { filename: string; key: string; updated_at?: string | number }[];
  activeArtifactKey?: string | null;
  onSelectArtifact?: (key: string) => void;
  activeSlideHash?: string;
  onActiveTabChange?: (tab: TabKey) => void;
  /**
   * Artifact view overlay. When set, the panel renders a layer on top of
   * the deck-html iframe with file metadata + Download button (PPTX) or
   * an inline iframe (image/PDF). The deck iframe stays mounted underneath
   * so returning preserves its scroll/slide state. `null` = no overlay.
   */
  artifactView?: { fileHash: string; filename: string; fileMime?: string; fileSize?: number } | null;
  onRebuildLegacy?: (version: string) => void;
  onCloseArtifactView?: () => void;
  /** Generic artifact viewer (PP main #87 alignment). Entries from
   *  signals.artifacts; PreviewTab picks the active one and uses its
   *  preview_url for the iframe. Falls back to legacy keyParam URL
   *  when undefined / empty. */
  artifactSignals?: import('../types').ArtifactSignal[];
  activeArtifactSignalId?: string | null;
}

const TABS: { key: TabKey; label: string; icon: React.FC<any> }[] = [
  { key: 'brief', label: 'Brief', icon: Graph },
  { key: 'storyboard', label: 'Storyboard', icon: Stack },
  { key: 'notes', label: 'Speaker Notes', icon: Notebook },
  { key: 'presentation', label: 'Presentation', icon: Monitor },
  { key: 'artifacts', label: 'Artifacts', icon: Stack },
  { key: 'insights', label: 'Insights', icon: Graph },
];

// Extract the first `# Heading` from markdown content, stripping a YAML
// frontmatter block first so `---\nname: x\n---` doesn't get mistaken
// for the title. Returns at most 60 characters (tabs are visually
// constrained), or null if no heading found.
// (aligns PP main #129 — Storyboard tab renames to doc title)
function extractMarkdownTitle(md: string | null | undefined): string | null {
  if (typeof md !== 'string' || !md.trim()) return null;
  let body = md;
  if (body.startsWith('---\n') || body.startsWith('---\r\n')) {
    const end = body.indexOf('\n---', 4);
    if (end > 0) body = body.slice(end + 4).replace(/^\r?\n/, '');
  }
  const m = body.match(/^#\s+(.+)$/m);
  if (!m) return null;
  const title = m[1].trim().slice(0, 60);
  return title.length > 0 ? title : null;
}

export default function PreviewPanel(props: PreviewPanelProps) {
  return <PreviewPanelBody key={props.deckId || 'no-deck'} {...props} />;
}

function PreviewPanelBody({
  phase, presentationReadOnly = false, deckId, workspaceId, deckTitle, deckMode, deckTheme, onDeckChanged, onReferenceSlide,
  isGenerating, currentActivity, onAdvancePhase,
  planMarkdown, onPlanMarkdownChange, onPlanSave,
  audienceInfo, goalInfo, keyMessages, maxPhaseReached, previewRefreshKey, contextVersion, contextItems, contextHydratedDeckId,
  planTask, previewTask, notesTask, planSaveStatus, planUpdatedAt,
  planVersions = [], activePlanKey, onSelectPlanVersion,
  notesMarkdown, onNotesChange, onNotesSave, notesSaveStatus, notesUpdatedAt,
  notesVersions = [], activeNotesKey, onSelectNotesVersion,
  previewGeneratingNoHTML, artifacts = [], activeArtifactKey, onSelectArtifact, activeSlideHash, onActiveTabChange,
  artifactView = null, onCloseArtifactView, onRebuildLegacy,
  artifactSignals, activeArtifactSignalId,
}: PreviewPanelProps) {
  const phaseToTab = (p: Phase | string): TabKey => {
    if (p === 'preview') return 'presentation';
    if (p === 'plan' || p === 'plan-ready') return 'storyboard';
    return 'brief'; // gather phase
  };
  const [requestedTab, setActiveTab] = useState<TabKey | null>(null);
  const [cfSlides, setCfSlides] = useState<SlideRow[]>([]);
  const [cfVersionName, setCfVersionName] = useState<string>('');
  const hasReachedPreview = (maxPhaseReached || phase) === 'preview';
  const hasPreview = (previewRefreshKey || 0) > 0 || hasReachedPreview;
  const presentationVisible = hasPreview || !!previewTask || cfSlides.length > 0 || !!artifactSignals?.length;
  const storyboardVisible = !!planMarkdown.trim() || !!planTask || cfSlides.length > 0 || phase === 'plan-ready';
  const notesVisible = !!notesMarkdown.trim() || !!notesTask;
  const briefVisible = true; // Brief stays reachable with an explicit empty state.
  const storyboardTitle = extractMarkdownTitle(planMarkdown);
  const visibleTabs = TABS.filter(t =>
    t.key === 'brief' ? briefVisible :
    t.key === 'storyboard' ? storyboardVisible :
    t.key === 'notes' ? notesVisible : presentationVisible
  ).map(t => t.key === 'storyboard' && storyboardTitle ? { ...t, label: storyboardTitle } : t);
  const preferredTab: TabKey = presentationVisible ? 'presentation' : storyboardVisible ? 'storyboard' : notesVisible ? 'notes' : 'brief';
  const activeTab: TabKey = requestedTab && visibleTabs.some(t => t.key === requestedTab) ? requestedTab : preferredTab;
  const tabsRef = useRef<HTMLDivElement>(null);
  const [viewerTarget, setViewerTarget] = useState<ViewerTarget | null>(null);
  const [showBriefing, setShowBriefing] = useState(false);

  // Initial selection follows loaded work, rather than a potentially stale phase.
  // A real phase transition can then move the user to the task's result.
  const previousPhase = useRef(phase);
  useEffect(() => {
    if (previousPhase.current !== phase) setActiveTab(phaseToTab(phase));
    previousPhase.current = phase;
  }, [phase]);

  // Notify parent when active tab changes (for layout ratio adjustment)
  useEffect(() => {
    onActiveTabChange?.(activeTab);
  }, [activeTab, onActiveTabChange]);
  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) return;
    const reveal = () => tabs.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({block:'nearest',inline:'nearest'});
    const observer = new ResizeObserver(reveal); observer.observe(tabs); reveal();
    return () => observer.disconnect();
  }, [activeTab]);


  // Build-mode picker fully retired in Stage 4 of #87 alignment.
  // The agent picks per-turn from user intent; safeAdvancePhase is
  // now a straight pass-through.
  const safeAdvancePhase = useCallback((target: Phase) => {
    onAdvancePhase?.(target);
  }, [onAdvancePhase]);

  // Auto-switch to preview tab when preview task starts or completes
  const prevPreviewTask = useRef(previewTask);
  useEffect(() => {
    // Switch when build starts
    if (previewTask === 'create' && !prevPreviewTask.current) {
      setActiveTab('presentation');
    }
    // Switch when build completes
    const wasBuilding = !!prevPreviewTask.current;
    const nowDone = !previewTask;
    if (wasBuilding && nowDone && hasReachedPreview) {
      setActiveTab('presentation');
    }
    prevPreviewTask.current = previewTask;
  }, [previewTask, hasReachedPreview]);

  const tabActionStyle: React.CSSProperties = {
    padding: '3px 8px', background: 'none', border: '1px solid var(--border)',
    borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3,
    color: 'var(--text-muted)', fontSize: 'var(--fs-2xs)', fontFamily: 'inherit', transition: 'all 0.15s',
  };

  // Editor edit mode — shared by storyboard and notes tabs
  const [mdEditing, setMdEditing] = useState(false);
  const [mdDirty, setMdDirty] = useState(false);
  const mdSnapshotRef = React.useRef('');

  const startEditing = () => {
    mdSnapshotRef.current = activeTab === 'storyboard' ? planMarkdown : notesMarkdown;
    setMdEditing(true);
    setMdDirty(false);
  };
  const saveEditing = () => {
    const md = activeTab === 'storyboard' ? planMarkdown : notesMarkdown;
    const saveFn = activeTab === 'storyboard' ? onPlanSave : onNotesSave;
    saveFn?.(md);
    setMdEditing(false);
    setMdDirty(false);
    const what = activeTab === 'storyboard' ? 'storyboard' : 'speaker notes';
    const msg = activeTab === 'storyboard' ? 'I updated the storyboard' : 'I updated the speaker notes';
    window.dispatchEvent(new CustomEvent('pp:edit-saved', { detail: { what: msg } }));
  };
  const cancelEditing = () => {
    const changeFn = activeTab === 'storyboard' ? onPlanMarkdownChange : onNotesChange;
    changeFn(mdSnapshotRef.current);
    setMdEditing(false);
    setMdDirty(false);
  };
  // Reset edit mode when switching tabs
  React.useEffect(() => { setMdEditing(false); setMdDirty(false); }, [activeTab]);

  // Parse storyboard from planMarkdown — if it has the storyboard header, render card view
  const storyboardData = useMemo(() => {
    if (!planMarkdown || !planMarkdown.includes('=== STORYBOARD ===')) return null;
    const parsed = parseStoryboard(planMarkdown);
    if (parsed.slides.length === 0) return null;
    return parsed;
  }, [planMarkdown]);

  const storyCards: StoryCard[] = useMemo(() => {
    if (!storyboardData) return [];
    return storyboardData.slides.map(s => ({
      id: `slide-${s.num}`,
      slideNum: s.num,
      title: s.title,
      body: s.body.join('\n'),
      tag: s.type,
    }));
  }, [storyboardData]);

  const handleCardsChange = useCallback((cards: StoryCard[]) => {
    if (!storyboardData) return;
    const updatedSlides = cards.map(c => ({
      num: c.slideNum,
      type: c.tag,
      title: c.title,
      body: c.body.split('\n'),
      notes: storyboardData.slides.find(s => s.num === c.slideNum)?.notes || [],
    }));
    const md = serializeStoryboard({ meta: storyboardData.meta, slides: updatedSlides });
    onPlanMarkdownChange(md);
  }, [storyboardData, onPlanMarkdownChange]);

  // CF-native storyboard source: when the agent has materialised a
  // presentation version, fetch its slides and render those as
  // cards. This takes priority over PP's planMarkdown-derived
  // storyboard once a version exists.
  useEffect(() => {
    if (!deckId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`/api/decks/${deckId}/versions`);
        const d = await r.json().catch(() => null);
        if (cancelled) return;
        const current = d?.data?.current;
        if (!d?.ok || !current) {
          setCfSlides([]);
          setCfVersionName('');
          return;
        }
        setCfVersionName(current);
        const v = await getVersion(deckId, current);
        if (!cancelled) setCfSlides(v.slides);
      } catch {
        if (!cancelled) setCfSlides([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deckId, previewRefreshKey]);

  const cfStoryCards: StoryCard[] = useMemo(
    () =>
      cfSlides.map((s) => ({
        id: `${cfVersionName}-${s.slide_num}`,
        slideNum: s.slide_num,
        title: s.title,
        body: s.body,
        tag: '',
      })),
    [cfSlides, cfVersionName],
  );

  const handleCfCardsChange = useCallback(
    (cards: StoryCard[]) => {
      if (!deckId || !cfVersionName) return;
      // Title/body edits → per-slide saveSlide. Reorder/delete in
      // the card view stays local-only for now; the iframe HTML
      // editor is the authoritative reorder path.
      const beforeBy = new Map(cfSlides.map((s) => [s.slide_num, s]));
      cards.forEach((c) => {
        const before = beforeBy.get(c.slideNum);
        const titleChanged = !before || before.title !== c.title;
        const bodyChanged = !before || before.body !== c.body;
        if (!titleChanged && !bodyChanged) return;
        const patch: { slide_num: number; title?: string; body?: string } = {
          slide_num: c.slideNum,
        };
        if (titleChanged) patch.title = c.title;
        if (bodyChanged) patch.body = c.body;
        saveSlide(deckId, cfVersionName, patch).catch((err) => {
          if (err instanceof AuthExpiredError) return;
          notifyError(
            `Save slide failed: ${(err as Error)?.message || 'network error'}`,
          );
        });
      });
      // Optimistic local merge.
      setCfSlides(
        cards.map((c) => ({
          presentation_name: cfVersionName,
          slide_num: c.slideNum,
          title: c.title,
          body: c.body,
          updated_at: Date.now(),
        })),
      );
    },
    [deckId, cfVersionName, cfSlides],
  );

  // Shared activity bubble — one for all tasks, lives at panel level
  const activeTaskName = planTask ? `plan:${planTask}` : notesTask ? `notes:${notesTask}` : previewTask ? `preview:${previewTask}` : null;
  const taskLabel = planTask ? (planTask === 'create' ? 'Planning...' : 'Updating plan...')
    : notesTask ? (notesTask === 'create' ? 'Writing notes...' : 'Updating notes...')
    : previewTask ? (previewTask === 'create' ? 'Building slides...' : 'Updating slides...')
    : null;
  const [taskElapsed, setTaskElapsed] = useState(0);
  useEffect(() => {
    if (!activeTaskName) { setTaskElapsed(0); return; }
    const t0 = Date.now();
    const iv = setInterval(() => setTaskElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [activeTaskName]);
  const formatElapsed = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}:${String(sec).padStart(2, '0')}` : `${sec}s`;
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative', background: 'var(--bg-primary)' }}>
      {/* Shared activity bubble — centered at bottom */}
      {activeTaskName && (
        <div style={{
          position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 20,
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '6px 16px',
          background: 'var(--ds-overlay)',
          borderRadius: 16,
          boxShadow: 'none',
          pointerEvents: 'none',
          animation: 'bubble-in 0.3s ease',
        }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%',
            border: '1.5px solid var(--ds-overlay)',
            borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
          }} />
          <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ds-overlay)', fontWeight: 300 }}>
            {taskLabel || 'Working...'}
          </span>
          <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--ds-overlay)', fontVariantNumeric: 'tabular-nums' }}>
            {formatElapsed(taskElapsed)}
          </span>
        </div>
      )}

      <style>{`@keyframes bubble-in { from { opacity: 0; transform: translateX(-50%) translateY(6px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>

      {/* ── Panel content: concept cloud OR tabs ── */}
      {(() => {
        return (
          <>
            {/* Tab bar */}
            <div className="pp-preview-tabbar" style={{
              height: 40, display: 'flex', alignItems: 'center',
              padding: '0 12px 0 12px',
              background: 'var(--bg-surface)', flexShrink: 0,
            }}>
              <div ref={tabsRef} role="tablist" aria-label="Project content" className="tab-scroll pp-preview-tabs" style={{ display: 'flex', gap: 2, overflowX: 'auto', flex: 1, minWidth: 0, scrollbarWidth: 'none' }}>
              {visibleTabs.map(tab => {
                const isActive = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button" role="tab" id={`project-tab-${tab.key}`} aria-selected={isActive} aria-controls="project-content" tabIndex={isActive ? 0 : -1}
                    onKeyDown={e => {
                      const index = visibleTabs.findIndex(t => t.key === tab.key);
                      const next = e.key === 'Home' ? 0 : e.key === 'End' ? visibleTabs.length - 1 : e.key === 'ArrowRight' ? (index + 1) % visibleTabs.length : e.key === 'ArrowLeft' ? (index - 1 + visibleTabs.length) % visibleTabs.length : -1;
                      if (next < 0) return;
                      e.preventDefault(); setActiveTab(visibleTabs[next].key);
                      (tabsRef.current?.querySelectorAll<HTMLButtonElement>('[role=tab]')[next])?.focus();
                    }}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '6px 14px', fontSize: 'var(--fs-2xs)', fontWeight: isActive ? 500 : 400,
                      background: 'transparent',
                      color: isActive ? 'var(--ds-accent)' : 'var(--text-muted)',
                      border: 'none',
                      borderBottom: isActive ? '2px solid var(--ds-accent)' : '2px solid transparent',
                      borderRadius: 0,
                      cursor: 'pointer',
                      fontFamily: 'inherit', transition: 'all 0.2s',
                      flexShrink: 0,
                    }}
                  >
                    {tab.label}
                  </button>
                );
              })}
              </div>

              {/* Panel-specific actions — fused into tab bar */}
              <div className="pp-tab-actions" style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, paddingLeft: 8, paddingRight: 4 }}>
                {/* Build mode pill — visible on every tab EXCEPT
                    preview. The preview tab renders its own action
                    group that absolutely floats up into this same
                    row at `top:-32; right:4` (Versions / Edit /
                    HTML / PPTX / PDF / Share / Fullscreen). The two
                    right-edge groups would otherwise overlap — the
                    pill ends up sitting underneath Share/Fullscreen.
                    On preview tab the mode is implied by the inner
                    toolbar (PPTX-download vs HTML-download); the
                    user can switch to Storyboard or ConceptCloud
                    to change mode if needed.

                    Stage 1 of PP main #87 alignment: Pick mode pill
                    hidden — the agent picks per-turn, no user pre-pick
                    needed. Pill removal stage; full generic-viewer
                    refactor in upcoming stage. */}
                {/* Save status for storyboard/notes */}
                {(activeTab === 'storyboard' || activeTab === 'notes') && (() => {
                  const status = activeTab === 'storyboard' ? planSaveStatus : notesSaveStatus;
                  return status === 'saving' ? (
                    <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>Saving...</span>
                  ) : status === 'saved' ? (
                    <span style={{ fontSize: 'var(--fs-2xs)', color: 'var(--ds-success)' }}>Saved</span>
                  ) : null;
                })()}

                {/* Edit / Save / Cancel for storyboard/notes */}
                {(activeTab === 'storyboard' || activeTab === 'notes') && (() => {
                  const hasContent = activeTab === 'storyboard' ? !!planMarkdown : !!notesMarkdown;
                  if (!hasContent) return null;
                  const saving = (activeTab === 'storyboard' ? planSaveStatus : notesSaveStatus) === 'saving';
                  if (mdEditing) return (
                    <>
                      <button onClick={saveEditing} disabled={!mdDirty || saving} style={{ ...tabActionStyle, background: 'var(--ds-accent)', borderColor: 'var(--ds-accent)', color: 'var(--ds-on-accent)', fontWeight: 600, opacity: (!mdDirty || saving) ? 0.5 : 1 }}>
                        <FloppyDisk size={12} weight="bold" /> {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button onClick={cancelEditing} style={tabActionStyle}>Cancel</button>
                    </>
                  );
                  return <button onClick={startEditing} style={tabActionStyle}><PencilSimple size={12} /> Edit</button>;
                })()}

                {/* MD download for storyboard/notes */}
                {activeTab === 'storyboard' && planMarkdown && (
                  <button onClick={() => {
                    const filename = activePlanKey?.split('/').pop() || 'storyboard.md';
                    const blob = new Blob([`# Plan\n\n${planMarkdown}`], { type: 'text/markdown' });
                    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
                  }} style={tabActionStyle} title="Download">
                    <DownloadSimple size={12} weight="light" /> MD
                  </button>
                )}
                {activeTab === 'notes' && notesMarkdown && (
                  <button onClick={() => {
                    const filename = activeNotesKey?.split('/').pop() || 'speaker-notes.md';
                    const blob = new Blob([`# Speaker Notes\n\n${notesMarkdown}`], { type: 'text/markdown' });
                    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
                  }} style={tabActionStyle} title="Download">
                    <DownloadSimple size={12} weight="light" /> MD
                  </button>
                )}
              </div>
            </div>

            {/* Tab content */}
            <div role="tabpanel" id="project-content" aria-labelledby={`project-tab-${activeTab}`} tabIndex={0} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth:0 }}>
              {activeTab === 'brief' && (
                <ConceptCloud
                  deckId={deckId || ''}
                  deckTitle={deckTitle}
                  audienceInfo={audienceInfo}
                  goalInfo={goalInfo}
                  keyMessages={keyMessages}
                  onClose={() => {}}
                  contextVersion={contextVersion}
                  prefetchedContext={contextItems}
                  contextHydratedDeckId={contextHydratedDeckId}
                />
              )}

              {activeTab === 'storyboard' && (
                cfStoryCards.length > 0 ? (
                  <StoryboardPanel
                    cards={cfStoryCards}
                    allowStructureEdits={false}
                    isGenerating={!!previewTask || !!planTask}
                    currentActivity={currentActivity}
                    onCardsChange={handleCfCardsChange}
                    onApproveGenerate={() => safeAdvancePhase('preview')}
                    hasGeneratedBefore={hasReachedPreview}
                  />
                ) : storyCards.length > 0 ? (
                  <StoryboardPanel
                    cards={storyCards}
                    audience={storyboardData?.meta.audience}
                    narrative={storyboardData?.meta.narrative}
                    isGenerating={!!planTask}
                    currentActivity={currentActivity}
                    onCardsChange={handleCardsChange}
                    onApproveGenerate={() => safeAdvancePhase('preview')}
                    hasGeneratedBefore={hasReachedPreview}
                  />
                ) : (
                  <MarkdownPlanEditor
                    markdown={planMarkdown}
                    onChange={(md) => { onPlanMarkdownChange(md); setMdDirty(md !== mdSnapshotRef.current); }}
                    onSave={onPlanSave}
                    onApprove={() => safeAdvancePhase('preview')}
                    isGenerating={isGenerating}
                    currentActivity={currentActivity}
                    hasBuiltBefore={hasReachedPreview}
                    planTask={planTask}
                    saveStatus={planSaveStatus}
                    updatedAt={planUpdatedAt}
                    hideToolbar
                    editable={mdEditing}
                    downloadFilename="storyboard.md"
                    versions={planVersions}
                    activeVersionKey={activePlanKey}
                    onSelectVersion={onSelectPlanVersion}
                  />
                )
              )}

              {activeTab === 'notes' && (
                <MarkdownPlanEditor
                  markdown={notesMarkdown}
                  onChange={(md) => { onNotesChange(md); setMdDirty(md !== mdSnapshotRef.current); }}
                  onSave={onNotesSave}
                  onApprove={() => {}}
                  isGenerating={isGenerating}
                  currentActivity={currentActivity}
                  planTask={notesTask}
                  saveStatus={notesSaveStatus}
                  hideApproveButton
                  editable={mdEditing}
                  title="Speaker Notes"
                  placeholder="Speaker notes will appear here..."
                  updatedAt={notesUpdatedAt}
                  hideToolbar
                  downloadFilename="speaker-notes.md"
                  versions={notesVersions}
                  activeVersionKey={activeNotesKey}
                  onSelectVersion={onSelectNotesVersion}
                />
              )}

              {activeTab === 'presentation' && deckId && (
                <PreviewTab
                  deckId={deckId}
                  readOnly={presentationReadOnly}
                  workspaceId={workspaceId}
                  deckTitle={deckTitle}
                  deckMode={deckMode ?? null}
                  deckTheme={deckTheme ?? null}
                  onDeckChanged={onDeckChanged}
                  isGenerating={isGenerating}
                  previewRefreshKey={previewRefreshKey}
                  hasReachedPreview={hasReachedPreview || cfSlides.length > 0 || !!artifactSignals?.length}
                  onAdvancePhase={onAdvancePhase}
                  previewTask={previewTask}
                  artifacts={artifacts.filter(a => a.filename?.endsWith('.html'))}
                  activeArtifactKey={activeArtifactKey}
                  onSelectArtifact={onSelectArtifact}
                  activeSlideHash={activeSlideHash}
                  artifactSignals={artifactSignals}
                  activeArtifactSignalId={activeArtifactSignalId}
                />
              )}

              {activeTab === 'artifacts' && deckId && (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                  <DeckCollabPanel deckId={deckId} onOpenSource={setViewerTarget} />
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <ArtifactLibraryPanel
                      deckId={deckId}
                      refreshKey={previewRefreshKey}
                      onOpen={setViewerTarget}
                      onRebuildLegacy={onRebuildLegacy}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'insights' && deckId && (
                <DeckInsightsPanel deckId={deckId} />
              )}

              {activeTab === 'presentation' && !deckId && (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                  Select a deck to get started
                </div>
              )}
            </div>
          </>
        );
      })()}

      {/* Briefing floating overlay */}
      {showBriefing && deckId && (
        <div style={{
          position: 'absolute', inset: 0, top: 40, zIndex: 30,
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Semi-transparent backdrop */}
          <div
            onClick={() => setShowBriefing(false)}
            style={{
              position: 'absolute', inset: 0,
              background: 'var(--ds-overlay)',
              backdropFilter: 'blur(2px)',
            }}
          />
          {/* Floating panel */}
          <div style={{
            position: 'relative', zIndex: 1,
            margin: 16, flex: 1, minHeight: 0,
            background: 'var(--bg-primary)',
            borderRadius: 0,
            border: '1px solid var(--border)',
            boxShadow: 'none',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
          }}>
            {/* Close button */}
            <button
              onClick={() => setShowBriefing(false)}
              style={{
                position: 'absolute', top: 8, right: 8, zIndex: 10,
                width: 28, height: 28, borderRadius: '50%',
                border: '1px solid var(--border)',
                background: 'var(--bg-surface)',
                cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)',
              }}
            >
              <X size={14} weight="light" />
            </button>
            <ConceptCloud
              deckId={deckId}
              deckTitle={deckTitle}
              audienceInfo={audienceInfo}
              goalInfo={goalInfo}
              keyMessages={keyMessages}
              onClose={() => setShowBriefing(false)}
              contextVersion={contextVersion}
              prefetchedContext={contextItems}
              contextHydratedDeckId={contextHydratedDeckId}
            />
          </div>
        </div>
      )}
      {/* Outer BuildModePicker removed — see generic-viewer Stage 4. */}
      {/* Right-panel artifact overlay (image / pdf / pptx download card).
          Lives at the PreviewPanel level — not inside PreviewTab — because
          PreviewTab only mounts on the 'presentation' tab and doesn't have
          artifactView in its prop scope. Putting the JSX inside PreviewTab
          (where it shipped via #62) compiled to a ReferenceError at runtime
          since artifactView wasn't in PreviewTab's destructure. */}
      {viewerTarget && (
        <ArtifactViewer
          target={viewerTarget}
          onClose={() => setViewerTarget(null)}
          onNavigate={setViewerTarget}
          onReference={(() => {
            if (!onReferenceSlide || !deckId || viewerTarget.deckId === deckId) return null;
            const m = viewerTarget.filename.match(/^slide-(\d+)\.(html|svg)$/);
            if (!m) return null;
            const slideNum = parseInt(m[1]!, 10);
            const title = viewerTarget.source?.deckTitle ?? viewerTarget.deckId;
            return () => onReferenceSlide({ deck_id: viewerTarget.deckId, version: viewerTarget.version, slide_num: slideNum, deck_title: title });
          })()}
        />
      )}
      {artifactView && deckId && (
        <ArtifactOverlay
          deckId={deckId}
          artifact={artifactView}
          onClose={() => onCloseArtifactView?.()}
        />
      )}
    </div>
  );
}

/**
 * Build a per-element text snapshot of every `.slide` in the deck.
 * Keyed by `<slideId> > <selector path>`. Values are trimmed innerText.
 *
 * Selector path uses `tagName + .firstClass + :nth-of-type(N)` when
 * there are multiple same-tag siblings — that's enough to tell apart
 * the items in a bulleted list without dragging in the full XPath.
 *
 * Elements with empty (post-trim) text are skipped — they can't have
 * a meaningful "before → after" diff and would just bloat the result.
 *
 * Used by the in-place edit flow: snapshot on edit-start, snapshot
 * again on save, diff at element granularity for the [user-edit]
 * agent message.
 */
function snapshotSlideElements(root: Document | HTMLElement): Record<string, string> {
  const snap: Record<string, string> = {};
  const slides = root.querySelectorAll('.slide');
  slides.forEach((slide) => {
    const slideId = (slide as HTMLElement).id;
    if (!slideId) return;
    const all = slide.querySelectorAll('*');
    all.forEach((el) => {
      const text = ((el as HTMLElement).innerText || el.textContent || '').trim();
      if (!text) return;
      // Skip container elements whose text is just the concatenation
      // of children — we want leaf-ish elements that actually carry
      // a user-edited string. Heuristic: element has no child elements,
      // OR all of its children are inline-style elements (br/span/em/
      // strong/a/code) with their own text already captured.
      const childEls = (el as HTMLElement).children;
      if (childEls.length > 0) {
        const allInline = Array.from(childEls).every(c => {
          const t = c.tagName.toLowerCase();
          return ['br', 'span', 'em', 'strong', 'b', 'i', 'a', 'code', 'small', 'sub', 'sup'].includes(t);
        });
        if (!allInline) return;
      }
      const tag = el.tagName.toLowerCase();
      const cls = (el as HTMLElement).className && typeof (el as HTMLElement).className === 'string'
        ? '.' + (el as HTMLElement).className.split(/\s+/).filter(Boolean)[0]
        : '';
      let nth = '';
      const parent = (el as HTMLElement).parentElement;
      if (parent) {
        const sameTagSibs = Array.from(parent.children).filter(c => c.tagName === el.tagName);
        if (sameTagSibs.length > 1) {
          const idx = sameTagSibs.indexOf(el as HTMLElement) + 1;
          nth = `:nth-of-type(${idx})`;
        }
      }
      const path = `${tag}${cls || ''}${nth}`;
      const key = `${slideId} > ${path}`;
      snap[key] = text.slice(0, 400);
    });
  });
  return snap;
}

/** Compute the per-element diff between two snapshots. Each item is
 *  "added", "removed", or "changed". Caller formats for display. */
function diffSlideElements(
  before: Record<string, string>,
  after: Record<string, string>,
): Array<{ key: string; kind: 'added' | 'removed' | 'changed'; before: string; after: string }> {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const out: Array<{ key: string; kind: 'added' | 'removed' | 'changed'; before: string; after: string }> = [];
  keys.forEach((k) => {
    const b = before[k];
    const a = after[k];
    if (b === undefined && a !== undefined) out.push({ key: k, kind: 'added', before: '', after: a });
    else if (a === undefined && b !== undefined) out.push({ key: k, kind: 'removed', before: b, after: '' });
    else if (b !== a) out.push({ key: k, kind: 'changed', before: b || '', after: a || '' });
  });
  return out;
}

/** Preview sub-component — renders iframe + toolbar, no overlays (those are in parent) */
function formatRelativeTime(ts?: string | number | null): string {
  if (!ts) return '';
  const d = typeof ts === 'number' ? new Date(ts) : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function appendPreviewQuery(url: string, query: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
}

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** PowerPoint-style speaker-notes pane (PP-main parity gap C).
 *
 * Mounted under the deck iframe in the Presentation view. Fetches
 * `slide-NN.note.md` for the visible slide whenever it changes and
 * renders the markdown in a collapsible drawer. The build talents
 * (pp-visual-designer scaffolds, pp-speaker-writer fills) already write
 * these files; this is the read-only viewer.
 *
 * Default state is expanded; the toggle is persisted per-deck in
 * localStorage. When no note exists for a slide the endpoint returns
 * 204 and we show a placeholder.
 */
function SpeakerNotesPane({
  deckId,
  slideNum,
}: {
  deckId: string;
  slideNum: number | null;
}) {
  const storageKey = `pp:notes-pane:expanded:${deckId}`;
  const [expanded, setExpanded] = useState<boolean>(() => {
    try {
      const v = localStorage.getItem(storageKey);
      return v == null ? true : v === '1';
    } catch { return true; }
  });
  const [md, setMd] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    try { localStorage.setItem(storageKey, expanded ? '1' : '0'); } catch {}
  }, [storageKey, expanded]);

  useEffect(() => {
    if (!slideNum) { setMd(''); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setMd('');
    // No ?version= — the backend resolves to deck.current_version, which
    // stays in sync with the displayed version because the version
    // selector always persists its choice via PUT /versions/current.
    fetch(`/api/decks/${deckId}/notes/slide/${slideNum}`, { credentials: 'include' })
      .then(async (r) => {
        if (cancelled) return '';
        if (r.status === 204 || !r.ok) return '';
        return r.text();
      })
      .then((t) => { if (!cancelled) setMd(t); })
      .catch(() => { if (!cancelled) setMd(''); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [deckId, slideNum]);

  // Fixed pane height when expanded; body scrolls internally so the pane
  // doesn't grow/shrink with each slide's note length.
  const COLLAPSED_HEIGHT = 32;
  const EXPANDED_HEIGHT = '32vh';
  // Memo BEFORE the early return — hooks must run unconditionally.
  // Explicit options so rendering doesn't depend on ChatPanel's global
  // marked.setOptions being loaded first.
  const html = useMemo(
    () => (md ? (marked.parse(md, { breaks: true, gfm: true }) as string) : ''),
    [md],
  );

  if (!slideNum) return null;

  return (
    <div style={{
      flexShrink: 0,
      borderTop: '1px solid var(--border)',
      background: 'var(--bg-surface)',
      height: expanded ? EXPANDED_HEIGHT : COLLAPSED_HEIGHT,
      overflow: 'hidden',
      transition: 'height 0.18s ease',
      boxSizing: 'border-box',
    }}>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 12px', height: COLLAPSED_HEIGHT, cursor: 'pointer', userSelect: 'none',
          fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
          boxSizing: 'border-box',
        }}
        title={expanded ? 'Collapse speaker notes' : 'Expand speaker notes'}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Notebook size={14} weight="regular" />
          Speaker Notes — slide {slideNum}
        </span>
        <span style={{ opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}>
          {expanded ? '▾' : '▸'}
        </span>
      </div>
      {expanded && (
        <div
          className="pp-speaker-notes-body"
          style={{
            padding: '8px 16px 14px',
            overflowY: 'auto',
            height: `calc(${EXPANDED_HEIGHT} - ${COLLAPSED_HEIGHT}px)`,
            boxSizing: 'border-box',
            fontSize: 'var(--fs-base)',
            lineHeight: 1.55,
            color: 'var(--text-primary)',
          }}
        >
          {loading ? (
            <em style={{ color: 'var(--text-muted)' }}>Loading…</em>
          ) : md ? (
            <div dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <em style={{ color: 'var(--text-muted)' }}>
              No notes for this slide yet — the agent writes them on the next build.
            </em>
          )}
        </div>
      )}
    </div>
  );
}

function PreviewTab({
  deckId, readOnly = false, workspaceId, deckTitle, deckMode, deckTheme, onDeckChanged,
  isGenerating, previewRefreshKey,
  hasReachedPreview, onAdvancePhase, previewTask,
  artifacts = [], activeArtifactKey, onSelectArtifact,
  activeSlideHash,
  artifactSignals, activeArtifactSignalId,
}: {
  deckId: string;
  readOnly?: boolean;
  workspaceId?: string | null;
  deckTitle?: string;
  deckMode?: 'html' | 'pptx' | null;
  deckTheme?: 'dify-default' | 'dify-dark' | null;
  onDeckChanged?: () => void;
  isGenerating: boolean;
  previewRefreshKey?: number;
  hasReachedPreview: boolean;
  onAdvancePhase?: (phase: Phase) => void;
  previewTask?: 'create' | 'update' | null;
  artifacts?: { filename: string; key: string; updated_at?: string | number }[];
  activeArtifactKey?: string | null;
  onSelectArtifact?: (key: string) => void;
  activeSlideHash?: string;
  artifactSignals?: import('../types').ArtifactSignal[];
  activeArtifactSignalId?: string | null;
}) {
  const hasPreview = (previewRefreshKey || 0) > 0 || hasReachedPreview;
  const autoTriggered = useRef(false);
  const [showShare, setShowShare] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showBrand, setShowBrand] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [editMode, setEditMode] = useState(false);
  // Speaker-notes pane gate. Read from /api/health (same public feature
  // surface the LoginPage uses). Defaults off until confirmed true so an
  // admin who disabled feature_speaker_notes_pane never sees a flash.
  const [notesPaneEnabled, setNotesPaneEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/health', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (!cancelled) setNotesPaneEnabled(!!d?.features?.speaker_notes_pane); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [pptxExporting, setPptxExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgressState | null>(null);
  const exportProgressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  // Toolbar dropdown menus. Export folds the downloads + share into one
  // trigger; More folds import/brand/members. This keeps the tab-bar
  // action row compact so it no longer overlaps the version selector /
  // tab title at narrower preview widths.
  const [exportOpen, setExportOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  // 1-based slide currently visible in the iframe. Tracked here (lifted
  // out of SlideNav) so the presenter-mode handler can land the
  // audience window on the right slide and broadcast subsequent nav.
  const [currentSlideNum, setCurrentSlideNum] = useState<number>(1);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const getReadyPreviewDocument = useCallback((): Document | null => {
    const doc = iframeRef.current?.contentDocument ?? null;
    if (!doc) return null;
    if (!doc.querySelector('#pp-deck')) return null;
    if (doc.querySelectorAll('.slide').length === 0) return null;
    return doc;
  }, []);

  const clearExportProgressTimer = useCallback(() => {
    if (exportProgressTimerRef.current) {
      clearTimeout(exportProgressTimerRef.current);
      exportProgressTimerRef.current = null;
    }
  }, []);
  useEffect(() => () => clearExportProgressTimer(), [clearExportProgressTimer]);

  const beginExportProgress = useCallback((kind: ExportKind, label: string) => {
    clearExportProgressTimer();
    setExportProgress({ kind, label, progress: 4, status: 'running' });
  }, [clearExportProgressTimer]);

  const updateExportProgress = useCallback((kind: ExportKind, label: string, progress: number) => {
    setExportProgress({ kind, label, progress, status: 'running' });
  }, []);

  const settleExportProgress = useCallback((
    kind: ExportKind,
    label: string,
    status: 'complete' | 'error',
  ) => {
    clearExportProgressTimer();
    setExportProgress({ kind, label, progress: 100, status });
    exportProgressTimerRef.current = setTimeout(() => {
      setExportProgress(null);
      exportProgressTimerRef.current = null;
    }, status === 'complete' ? 1400 : 2600);
  }, [clearExportProgressTimer]);

  const exportBusy = pptxExporting || pdfExporting || downloading;

  const handleDomPptxExport = useCallback(async () => {
    if (exportBusy) return;
    const iframe = iframeRef.current;
    if (!iframe || !getReadyPreviewDocument()) {
      notifyError('Preview is still loading — wait for the slides to appear, then try again.');
      return;
    }
    setExportOpen(false);
    beginExportProgress('pptx', 'Preparing PPTX export');
    setPptxExporting(true);
    try {
      const { downloadBlob, exportPreviewIframeToPptx, pptxFilename } = await import(
        '../export/domToPptx'
      );
      const result = await exportPreviewIframeToPptx(iframe, {
        preferEditable: true,
        onProgress: (update) => updateExportProgress('pptx', update.label, update.progress),
      });
      downloadBlob(result.blob, pptxFilename(deckTitle));
      settleExportProgress(
        'pptx',
        result.mode === 'screenshot' ? 'Screenshot PPTX download started' : 'PPTX download started',
        'complete',
      );
      if (result.mode === 'screenshot') {
        notifyInfo('PPTX exported using screenshot fallback.');
      } else if (result.warnings.length > 0) {
        notifyInfo('PPTX exported with validation warnings.');
      } else {
        notifyInfo('PPTX exported from browser preview.');
      }
    } catch (err) {
      settleExportProgress('pptx', 'PPTX export failed', 'error');
      notifyError(`PPTX export failed: ${(err as Error)?.message || 'unknown error'}`);
    } finally {
      setPptxExporting(false);
    }
  }, [beginExportProgress, deckTitle, exportBusy, getReadyPreviewDocument, settleExportProgress, updateExportProgress]);

  const handleDomPdfExport = useCallback(async () => {
    if (exportBusy) return;
    const iframe = iframeRef.current;
    if (!iframe || !getReadyPreviewDocument()) {
      notifyError('Preview is still loading — wait for the slides to appear, then try again.');
      return;
    }
    setExportOpen(false);
    beginExportProgress('pdf', 'Preparing PDF export');
    setPdfExporting(true);
    try {
      const { downloadBlob, exportPreviewIframeToPdf, pdfFilename } = await import(
        '../export/domToPptx'
      );
      const result = await exportPreviewIframeToPdf(iframe, {
        onProgress: (update) => updateExportProgress('pdf', update.label, update.progress),
      });
      downloadBlob(result.blob, pdfFilename(deckTitle));
      settleExportProgress('pdf', 'PDF download started', 'complete');
      if (result.warnings.length > 0) {
        notifyInfo('PDF exported with validation warnings.');
      } else {
        notifyInfo('PDF exported from browser preview.');
      }
    } catch (err) {
      settleExportProgress('pdf', 'PDF export failed', 'error');
      notifyError(`PDF export failed: ${(err as Error)?.message || 'unknown error'}`);
    } finally {
      setPdfExporting(false);
    }
  }, [beginExportProgress, deckTitle, exportBusy, getReadyPreviewDocument, settleExportProgress, updateExportProgress]);
  // Snapshot of slide innerText at the moment "Edit" is clicked, keyed
  // by slide id. Diffed against the post-save HTML to produce a richer
  // [user-edit] chat message instead of the generic "please review".
  const editSnapshotRef = useRef<Record<string, string> | null>(null);
  // Anchor-comment mode: when on, clicks inside the deck iframe open a
  // popover anchored to the clicked element so the user can ask the
  // agent for a change without typing the location themselves.
  const [commentMode, setCommentMode] = useState(false);
  const [commentPopover, setCommentPopover] = useState<null | {
    x: number; y: number; anchor: string; elementText: string;
  }>(null);
  const commentTextRef = useRef<HTMLTextAreaElement>(null);
  // Currently outlined element in comment mode — used to remove the
  // highlight class before applying it to a newly clicked element.
  const commentAnchorElRef = useRef<HTMLElement | null>(null);

  // Treat null mode as html (legacy decks).
  const effectiveMode: 'html' | 'pptx' = deckMode === 'pptx' ? 'pptx' : 'html';

  // safeAdvancePhase: pass-through. Mode picker fully retired in
  // Stage 4 of #87 alignment — agent picks per-turn from user intent.
  const safeAdvancePhase = useCallback((target: Phase) => {
    onAdvancePhase?.(target);
  }, [onAdvancePhase]);

  // ── Deck versions from API ──
  const [apiVersions, setApiVersions] = useState<{ key: string; title: string; slides: number; created_at: number; updated_at: number }[]>([]);
  const [currentVersion, setCurrentVersion] = useState('');
  // Read-count telemetry: dwell-debounced, batched (team-collab MVP).
  useSlideViewTracker(deckId ?? null, currentVersion, currentSlideNum);

  useEffect(() => {
    if (!deckId) return;
    apiFetch(`/api/decks/${deckId}/versions`)
      .then(async r => {
        const d = await r.json().catch(() => null);
        if (d?.ok && d.data) {
          setApiVersions(d.data.versions || []);
          setCurrentVersion(d.data.current || '');
        }
      })
      .catch(() => {}); // Passive list; AuthExpiredError already surfaced by apiFetch, network errors stay quiet
  }, [deckId, previewRefreshKey]);

  const hasMultipleVersions = apiVersions.length > 1;

  // Filter to assembled/monolithic decks (not individual slides or manifest)
  const assembledArtifacts = artifacts.filter(a =>
    a.filename === 'assembled.html' || (!a.filename?.startsWith('slide-') && !a.filename?.startsWith('manifest'))
  );
  // Priority: explicit selection → match current DB version → first assembled → first artifact
  const activeArtifact =
    (activeArtifactKey && assembledArtifacts.find(a => a.key === activeArtifactKey))
    || (currentVersion && assembledArtifacts.find(a => a.key.includes(`/${currentVersion}/`)))
    || assembledArtifacts[0]
    || artifacts[0];

  // Sync: when currentVersion loads from API and no explicit selection, select the matching artifact
  useEffect(() => {
    if (currentVersion && !activeArtifactKey && assembledArtifacts.length > 0) {
      const match = assembledArtifacts.find(a => a.key.includes(`/${currentVersion}/`));
      if (match) onSelectArtifact?.(match.key);
    }
  }, [currentVersion, assembledArtifacts.length]);

  // Derive deck folder from active artifact key
  const deckFolder = activeArtifact?.key?.split('/').slice(1, -1).join('/') || null;

  // Use custom event for edit-saved notification (avoids stale closure issues)

  // Listen for postMessage from editor iframe — save full assembled HTML
  useEffect(() => {
    function handleMessage(e: MessageEvent) {
      if (!e.data || e.data.type !== 'pp:html') return;
      const html = e.data.html;
      if (!html || !deckId) return;
      setSaving(true);
      apiFetch(`/api/decks/${deckId}/preview`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          html,
          deckFolder,
          editedSlide: typeof e.data.editedSlide === 'number'
            ? e.data.editedSlide
            : null,
          editedSlideTitle: e.data.editedSlideTitle ?? null,
          field: 'body',
        }),
      })
        .then(async r => {
          const data = await r.json().catch(() => ({ ok: false, error: 'Invalid server response' }));
          if (!data.ok) {
            notifyError(data.error ? `Save failed: ${data.error}` : 'Save failed — please try again');
            return;
          }
          setEditMode(false);
          // Diff the pre-edit snapshot against the saved HTML so the
          // chat message lists *what actually changed* instead of a
          // generic "please review". Falls back to the slide-info hint
          // if the snapshot is missing or no text diffs are detected.
          const before = editSnapshotRef.current || {};
          let after: Record<string, string> = {};
          try {
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            after = snapshotSlideElements(parsed);
          } catch {}
          const diffs = diffSlideElements(before, after);
          editSnapshotRef.current = null;
          const slideInfo = e.data.editedSlide
            ? `slide ${e.data.editedSlide}${e.data.editedSlideTitle ? ` "${e.data.editedSlideTitle}"` : ''}`
            : 'the presentation';
          const MAX_LIST = 15;
          const shown = diffs.slice(0, MAX_LIST);
          const overflow = diffs.length - shown.length;
          const formatLine = (d: { key: string; kind: 'added' | 'removed' | 'changed'; before: string; after: string }): string => {
            // key is "slide-03 > h1.title:nth-of-type(2)" — split for
            // readability in the agent message.
            const [slidePart, ...selParts] = d.key.split(' > ');
            const sel = selParts.join(' > ');
            if (d.kind === 'added')   return `- ${slidePart} -> ${sel}: (added) ${JSON.stringify(d.after.slice(0, 200))}`;
            if (d.kind === 'removed') return `- ${slidePart} -> ${sel}: (removed) ${JSON.stringify(d.before.slice(0, 200))}`;
            return `- ${slidePart} -> ${sel}: ${JSON.stringify(d.before.slice(0, 200))} -> ${JSON.stringify(d.after.slice(0, 200))}`;
          };
          let what: string;
          if (diffs.length === 0) {
            what = `[user-edit] Saved ${slideInfo} (no visible text changes detected). Please review.`;
          } else {
            const lines = shown.map(formatLine).join('\n');
            const tail = overflow > 0 ? `\n+ ${overflow} more` : '';
            what = `[user-edit] User edited ${diffs.length} element(s):\n${lines}${tail}\n\nThis captures the new text but not the user's intent. Ask the user what they want next — should this be finalized, propagated to other slides, or restyled? Do not assume; clarify first.`;
          }
          window.dispatchEvent(
            new CustomEvent('pp:edit-saved', {
              detail: {
                what,
                // Carry the slide number so the App-level listener can
                // anchor the iframe at that slide before it remounts
                // (editMode flip changes the iframe key). Without this
                // anchor the preview always jumps to slide 1 after save.
                slideNum:
                  typeof e.data.editedSlide === 'number'
                    ? e.data.editedSlide
                    : null,
              },
            }),
          );
        })
        .catch(err => {
          if (err instanceof AuthExpiredError) return; // Modal already shown
          notifyError(`Save failed: ${err?.message || 'network error'}`);
        })
        .finally(() => setSaving(false));
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [deckId, deckFolder]);

  // Auto-trigger builder when preview tab is shown but no preview exists.
  // When the deck's mode hasn't been picked, we still auto-trigger but
  // route through safeAdvancePhase, which opens the BuildModePicker
  // instead of POSTing /:id/phase directly.
  useEffect(() => {
    if (!hasPreview && !isGenerating && !autoTriggered.current && onAdvancePhase) {
      autoTriggered.current = true;
      safeAdvancePhase('preview');
    }
  }, [hasPreview, isGenerating, onAdvancePhase, safeAdvancePhase]);

  useEffect(() => {
    autoTriggered.current = false;
  }, [deckId]);

  // ── Slide-change subscription ──────────────────────────────────────
  // The embedded Liquid Glass dock (slide-nav-source.ts) fires
  // `pp:slide-changed` to window.parent on click, IO scroll-detect,
  // keyboard, and initial mount. We mirror its 1-based slide number
  // into currentSlideNum so the presenter-mode BroadcastChannel and
  // "anchor at slide" hash on edit-mode entry both stay in sync.
  // Validates payload strictly; iframe origin can be same-host
  // ('/preview') or local file:// (exported deck) so we accept '*'.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      const d = e.data;
      if (!d || typeof d !== 'object') return;
      if (d.type !== 'pp:slide-changed') return;
      const n = Number(d.slide);
      if (!Number.isFinite(n) || n < 1) return;
      setCurrentSlideNum(n);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // ── Presenter-mode sync ────────────────────────────────────────────
  // When the user opens the deck on a secondary monitor via the
  // Fullscreen action, the audience window subscribes to
  // `pp-deck-{deckId}` and follows slide changes. We always publish
  // the current slide here so the audience is ready before we even
  // know it exists — an empty channel is a no-op. We also listen for
  // `nav` messages from the audience (its own ArrowLeft/Right
  // keystrokes) and synthesize the same navigation in the main
  // window's iframe so both stay in sync.
  useEffect(() => {
    if (!deckId || typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel(`pp-deck-${deckId}`);
    const onMsg = (e: MessageEvent) => {
      const d = e.data;
      if (!d || typeof d !== 'object') return;
      if (d.type === 'nav') {
        // SlideNav's keyboard handler listens on the iframe DOCUMENT,
        // so the synthetic event must be dispatched there — an event
        // dispatched on the window object never reaches document-level
        // listeners.
        const doc = iframeRef.current?.contentWindow?.document;
        if (!doc) return;
        const key = d.direction === 'prev' ? 'ArrowLeft' : 'ArrowRight';
        try {
          doc.dispatchEvent(new KeyboardEvent('keydown', { key, bubbles: true }));
        } catch {}
      } else if (d.type === 'slide' && d.source === 'presenter') {
        // The presenter window navigated on its own (pill click /
        // scroll) — land the workspace iframe on the same slide so
        // speaker notes and the read tracker follow along.
        const n = Number(d.slide);
        if (!Number.isFinite(n) || n < 1) return;
        setCurrentSlideNum(n);
        const iframe = iframeRef.current;
        const win = iframe?.contentWindow;
        if (!win) return;
        try {
          const hash = `#slide-${String(n).padStart(2, '0')}`;
          if (win.location.hash !== hash) win.location.hash = hash;
        } catch {}
      }
    };
    ch.addEventListener('message', onMsg);
    return () => { ch.removeEventListener('message', onMsg); ch.close(); };
  }, [deckId]);

  useEffect(() => {
    if (!deckId || typeof BroadcastChannel === 'undefined') return;
    if (currentSlideNum == null || currentSlideNum < 1) return;
    const ch = new BroadcastChannel(`pp-deck-${deckId}`);
    try { ch.postMessage({ type: 'slide', slide: currentSlideNum }); } catch {}
    ch.close();
  }, [deckId, currentSlideNum]);


  // Anchor-comment mode: install a capture-phase click handler inside
  // the deck iframe so the next click opens a popover instead of
  // navigating. Records the clicked element's slide id + tag/class
  // selector + a short text preview, then positions the popover in
  // main-window coords via iframe.getBoundingClientRect() + element
  // rect math (so it stays anchored even with the chat sidebar).
  useEffect(() => {
    if (!commentMode) { setCommentPopover(null); return; }
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!doc) return;
    const styleEl = doc.createElement('style');
    // Crosshair cursor (whole tree) + outline on the clicked anchor
    // so the user can visually confirm which element will receive
    // their comment. Outline-offset prevents clipping the element's
    // own border/shadow.
    styleEl.textContent = [
      '*, *::before, *::after { cursor: crosshair !important; }',
      '.pp-comment-anchor {',
      '  outline: 2px solid var(--brand, var(--ds-accent)) !important;',
      '  outline-offset: 4px !important;',
      '  transition: outline 0.12s ease !important;',
      '}',
    ].join('\n');
    doc.head?.appendChild(styleEl);
    const clearAnchorHighlight = () => {
      const prev = commentAnchorElRef.current;
      if (prev) {
        try { prev.classList.remove('pp-comment-anchor'); } catch {}
        commentAnchorElRef.current = null;
      }
    };
    const onClick = (ev: MouseEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      const target = ev.target as HTMLElement | null;
      if (!target) return;
      let slideEl: HTMLElement | null = target;
      while (slideEl && !slideEl.classList?.contains('slide')) slideEl = slideEl.parentElement;
      const slideId = slideEl?.id || '';
      const cls = (target.className && typeof target.className === 'string')
        ? '.' + target.className.split(/\s+/).filter(Boolean).slice(0, 2).join('.')
        : '';
      const tagDesc = target.tagName.toLowerCase() + cls;
      const textPreview = ((target as HTMLElement).innerText || target.textContent || '').trim().slice(0, 120);
      // Highlight the new target; drop the previous so we never
      // leave more than one outline behind.
      clearAnchorHighlight();
      try { target.classList.add('pp-comment-anchor'); } catch {}
      commentAnchorElRef.current = target;
      const elemRect = target.getBoundingClientRect();
      const iframeRect = iframe!.getBoundingClientRect();
      // Popup dimensions match the JSX below (maxWidth 360, ~200px tall).
      // Clamp so the popup stays inside the viewport — without this, a
      // click near the right or bottom edge of the preview pane pushes
      // the popup off-screen (aligns PP main #122).
      const POPUP_W = 360;
      const POPUP_H = 200;
      const PAD = 8;
      let x = iframeRect.left + elemRect.left;
      let y = iframeRect.top + elemRect.bottom + 8;
      x = Math.max(PAD, Math.min(x, window.innerWidth - POPUP_W - PAD));
      if (y + POPUP_H + PAD > window.innerHeight) {
        const flipped = iframeRect.top + elemRect.top - POPUP_H - 8;
        y = Math.max(PAD, flipped);
      }
      setCommentPopover({
        x, y,
        anchor: slideId ? `${slideId} → ${tagDesc}` : tagDesc,
        elementText: textPreview,
      });
      setTimeout(() => commentTextRef.current?.focus(), 30);
    };
    doc.addEventListener('click', onClick, true);
    return () => {
      try { doc.removeEventListener('click', onClick, true); } catch {}
      try { styleEl.remove(); } catch {}
      clearAnchorHighlight();
    };
  }, [commentMode]);

  // Dismiss the comment popover and drop the outline highlight, but
  // keep comment mode itself on — the user may want to pick a
  // different element right after cancelling. Toggling comment mode
  // off (via the toolbar) tears down the click listener separately.
  const closeCommentPopover = useCallback(() => {
    const prev = commentAnchorElRef.current;
    if (prev) {
      try { prev.classList.remove('pp-comment-anchor'); } catch {}
      commentAnchorElRef.current = null;
    }
    setCommentPopover(null);
  }, []);

  const sendComment = useCallback(() => {
    const text = commentTextRef.current?.value?.trim();
    if (!text || !commentPopover) return;
    const what = `[user-comment] anchor=${commentPopover.anchor}; element_text=${JSON.stringify(commentPopover.elementText)}; request=${JSON.stringify(text)}\n\nThe user clicked this element and asked for the change above. The anchor identifies *where* and the request states *what* — act directly (no need to re-confirm intent).`;
    window.dispatchEvent(new CustomEvent('pp:edit-saved', { detail: { what } }));
    closeCommentPopover();
    setCommentMode(false);
  }, [commentPopover, closeCommentPopover]);

  const previewKey = activeArtifact?.key;
  // For CF-native decks (no artifacts table) we use the version
  // name directly; for legacy artifact-based decks we keep the
  // existing key path. Either way, both end up mounting a fresh
  // iframe when the version flips.
  const effectivePreviewKey = previewKey || currentVersion || '';
  const keyParam = effectivePreviewKey
    ? `&key=${encodeURIComponent(effectivePreviewKey)}`
    : '';
  const editParam = editMode ? '&edit=1' : '';
  // Generic artifact viewer (PP main #87 alignment). When signals has
  // an active artifact, build the iframe URL from its preview_url
  // (which already encodes the artifact_id resolution server-side).
  // Falls back to the legacy keyParam construction otherwise so
  // pre-#78 decks and signals-less responses still preview.
  const activeArtifactSignal = artifactSignals?.find(
    (a) => a.id === activeArtifactSignalId,
  );
  const previewBaseUrl = activeArtifactSignal
    ? `${activeArtifactSignal.preview_url}&_t=${previewRefreshKey || 0}`
    : `/api/decks/${deckId}/preview?_t=${previewRefreshKey || 0}${keyParam}`;
  const previewUrl = `${previewBaseUrl}${editParam}${activeSlideHash || ''}`;
  const [frameState, setFrameState] = useState<'loading'|'ready'|'error'>('loading');
  const [frameRetry, setFrameRetry] = useState(0);
  const frameTimer = useRef<ReturnType<typeof setTimeout>|null>(null);
  useEffect(() => {
    setFrameState('loading');
    if (previewTask === 'create' && !artifacts.length && !activeArtifactSignal) return;
    frameTimer.current = setTimeout(() => setFrameState('error'), 15000);
    return () => { if (frameTimer.current) clearTimeout(frameTimer.current); };
  }, [previewUrl, frameRetry, !!previewTask]);
  const onFrameLoaded = () => {
    if (frameTimer.current) clearTimeout(frameTimer.current);
    try {
      const doc = iframeRef.current?.contentDocument;
      if (!doc) { setFrameState('ready'); return; } // External artifact owns its rendering.
      const text = doc.body?.textContent?.trim() || '';
      const expectedDeck = !activeArtifactSignal || activeArtifactSignal.kind === 'deck-group';
      let apiError = false;
      if (expectedDeck && text.startsWith('{')) { try { const body = JSON.parse(text); apiError = body.ok === false || !!body.error; } catch {} }
      const emptyDeck = expectedDeck && ((!!doc.querySelector('#pp-deck') && !doc.querySelector('.slide')) || text === 'No presentation generated yet.' || text === 'not found');
      const hasContent = !!text || !!doc.querySelector('img,svg,canvas,video');
      setFrameState(hasContent && !apiError && !emptyDeck ? 'ready' : 'error');
    } catch { setFrameState('ready'); }
  };


  // Presenter fullscreen opens a dedicated presentation surface, close
  // to the PP EKS projection flow: black shell, full-viewport deck
  // iframe (the ?presenter=1 assembly), and a click-to-enter-OS-
  // fullscreen overlay. The workspace iframe remains mounted and synced
  // through BroadcastChannel — presenter nav lands the workspace on the
  // same slide (speaker notes follow), and workspace nav drives the
  // presenter. If the Window Management API reports a second screen,
  // the popup is moved there after opening.
  const handleFullscreen = useCallback(async () => {
    if (!deckId) return;
    const padSlide = (n: number) => String(n).padStart(2, '0');
    const startHash = currentSlideNum && currentSlideNum > 0
      ? `#slide-${padSlide(currentSlideNum)}`
      : '';
    const channelName = `pp-deck-${deckId}`;
    const presenterPreviewUrl = appendPreviewQuery(previewBaseUrl, 'presenter=1');
    const presenterUrl = `${presenterPreviewUrl}${startHash}`;
    const presenter = window.open('', `pp-presenter-${deckId}`, 'popup=yes,width=1280,height=800');

    if (!presenter) {
      notifyError('Fullscreen window was blocked. Please allow popups for PitchPilot and try again.');
      return;
    }

    const safeTitle = (deckTitle || 'Deck').replace(/[<>"']/g, '');
    const frameSrc = escapeHtmlAttr(presenterUrl);
    presenter.document.open();
    presenter.document.write(`<!DOCTYPE html>
<html><head><title>Presenting - ${safeTitle}</title>
<meta charset="utf-8">
<style>
  html,body{margin:0;height:100%;overflow:hidden;background:#000;font-family:system-ui,sans-serif}
  iframe{width:100%;height:100%;border:0;display:block;background:#000}
  #fs-overlay{position:fixed;inset:0;z-index:9999;display:flex;align-items:center;justify-content:center;cursor:pointer;background:rgba(0,0,0,0.48);transition:opacity .22s ease}
  #fs-overlay.hidden{opacity:0;pointer-events:none}
  #fs-overlay .label{color:#fff;font:500 18px/1.4 system-ui,sans-serif;letter-spacing:0;padding:10px 22px;border:1px solid rgba(255,255,255,0.38);border-radius:10px;background:rgba(0,0,0,0.36);backdrop-filter:blur(6px)}
</style>
</head><body>
<iframe id="d" src="${frameSrc}" allowfullscreen></iframe>
<div id="fs-overlay"><span class="label">Click to present fullscreen</span></div>
<script>
  (function(){
    var deckFrame = document.getElementById('d');
    var overlay = document.getElementById('fs-overlay');
    var ch = typeof BroadcastChannel === 'function' ? new BroadcastChannel(${JSON.stringify(channelName)}) : null;
    var lastSlide = ${currentSlideNum || 1};
    function goTo(slide){
      if (!slide) return;
      lastSlide = slide;
      try {
        var win = deckFrame.contentWindow;
        if (win) {
          var hash = '#slide-' + String(slide).padStart(2, '0');
          if (win.location.hash !== hash) win.location.hash = hash;
        }
      } catch (e) {}
    }
    if (ch) {
      ch.onmessage = function(e){
        if (e.data && e.data.type === 'slide' && typeof e.data.slide === 'number') goTo(e.data.slide);
      };
    }
    overlay.addEventListener('click', function(){
      try {
        var p = document.documentElement.requestFullscreen();
        if (p && p.catch) p.catch(function(){});
      } catch (e) {}
      overlay.classList.add('hidden');
      setTimeout(function(){ overlay && overlay.parentNode && overlay.parentNode.removeChild(overlay); }, 350);
      // Land keyboard focus inside the deck so arrow keys hit
      // SlideNav's document-level handler directly.
      try { deckFrame.contentWindow && deckFrame.contentWindow.focus(); } catch (e) {}
    });
    if (ch) ch.postMessage({type:'presenter-open'});
    window.addEventListener('message', function(e){
      if (!e.data || e.data.type !== 'pp:slide-changed') return;
      var slide = Number(e.data.slide);
      if (!Number.isFinite(slide) || slide < 1) return;
      lastSlide = slide;
      if (ch) ch.postMessage({type:'slide', source:'presenter', slide:slide});
    });
    window.addEventListener('beforeunload', function(){ try{ if (ch) { ch.postMessage({type:'presenter-close'}); ch.close(); } }catch(e){} });
    window.addEventListener('keydown', function(e){
      if (!ch) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === 'PageDown') ch.postMessage({type:'nav', direction:'next'});
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp' || e.key === 'PageUp') ch.postMessage({type:'nav', direction:'prev'});
    });
  })();
</script>
</body></html>`);
    presenter.document.close();
    try { presenter.focus(); } catch {}

    const screenApi: any = (window as any).getScreenDetails;
    if (typeof screenApi === 'function') {
      try {
        const details = await screenApi.call(window);
        const screens: any[] = details?.screens || [];
        const currentLabel = details?.currentScreen?.label;
        const other = screens.find(s => s.label !== currentLabel)
          || (screens.length > 1 ? screens.find(s => s !== details.currentScreen) : null);
        if (other && !presenter.closed) {
          const left = other.availLeft ?? other.left ?? 0;
          const top = other.availTop ?? other.top ?? 0;
          const width = other.availWidth ?? other.width ?? 1280;
          const height = other.availHeight ?? other.height ?? 800;
          try { presenter.moveTo(left, top); } catch {}
          try { presenter.resizeTo(width, height); } catch {}
          try { presenter.focus(); } catch {}
        }
      } catch {}
    }
  }, [deckId, deckTitle, currentSlideNum, previewBaseUrl]);

  const btnStyle: React.CSSProperties = {
    padding: '4px 10px', background: 'none', border: '1px solid var(--border)',
    borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
    color: 'var(--text-secondary)', transition: 'all 0.15s',
    fontSize: 'var(--fs-2xs)', fontWeight: 400, fontFamily: 'inherit',
  };
  // Dropdown menu container + row styles, shared by Export / More.
  const menuStyle: React.CSSProperties = {
    position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50,
    background: 'var(--bg-primary)', border: '1px solid var(--border)',
    borderRadius: 10, minWidth: 180, padding: 4,
    boxShadow: '0 4px 20px var(--ds-shadow-color)',
    display: 'flex', flexDirection: 'column', gap: 2,
  };
  const menuItemStyle: React.CSSProperties = {
    padding: '7px 10px', background: 'none', border: 'none', borderRadius: 6,
    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
    color: 'var(--text-primary)', fontSize: 'var(--fs-sm)', fontWeight: 400,
    fontFamily: 'inherit', width: '100%', textAlign: 'left', textDecoration: 'none',
    whiteSpace: 'nowrap',
  };

  // No preview yet and not building — show prompt
  if (!hasPreview && !previewTask) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
        <div style={{ textAlign: 'center', maxWidth: 360 }}>
          <Sparkle size={20} weight="light" style={{ color: 'var(--text-muted)', margin: '0 auto 16px' }} />
          <h2 style={{ fontSize: 'var(--fs-lg)', fontWeight: 300, marginBottom: 8, color: 'var(--text-primary)' }}>
            Generate Preview
          </h2>
          <p style={{ fontSize: 'var(--fs-base)', color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.5 }}>
            Click below to generate a presentation from your current brief.
          </p>
          <button
            onClick={() => safeAdvancePhase('preview')}
            style={{
              padding: '8px 24px', fontSize: 'var(--fs-sm)', fontWeight: 400,
              background: 'var(--text-primary)', color: 'var(--bg-primary)', border: 'none', borderRadius: 4,
              cursor: 'pointer', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}
          >
            <Sparkle size={12} weight="light" />
            Start Building
          </button>
        </div>
        {/* BuildModePicker removed — Stage 4 of #87 alignment. */}
      </div>
    );
  }

  // Building in progress but no HTML yet — zen fire placeholder
  if (previewTask === 'create' && artifacts.length === 0) {
    return (
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-primary)', position: 'relative', overflow: 'hidden',
      }}>
        <PanelState kind="loading" title="Building your presentation" description="Your slides will appear here as soon as the build is ready. You can keep discussing changes in Chat." />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight:0 }}>
      {/* Presentation actions own a row so long tabs never overlap them. */}
      <div style={{
        minHeight: 48, display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        padding: '6px 12px', background: 'var(--ds-surface)', flexShrink: 0, borderBottom:'1px solid var(--ds-border)',
        position: 'relative', overflow: 'visible', zIndex: 50,
      }}>
        {/* Actions — positioned to float up into tab bar */}
        <div className="pp-tab-actions pp-presentation-actions" style={{ display: 'flex', flexWrap:'wrap', alignItems: 'center', gap: 4 }}>
          {apiVersions.length > 0 && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setSelectorOpen(!selectorOpen)}
                style={{
                  ...btnStyle,
                  padding: '4px 10px',
                }}
              >
                {currentVersion ? currentVersion.replace(/-\d+$/, '') : 'Versions'}
                {hasMultipleVersions && ' ▾'}
              </button>
              {selectorOpen && (
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 50,
                  background: 'var(--bg-primary)', border: '1px solid var(--border)',
                  borderRadius: 10, minWidth: 240, maxHeight: 240, overflowY: 'auto',
                  boxShadow: '0 4px 20px var(--ds-shadow-color)',
                }}>
                  {apiVersions.map(v => {
                    const isActive = v.key === currentVersion;
                    const subtitle = v.title && v.title !== v.key ? v.title : null;
                    return (
                      <div
                        key={v.key}
                        style={{
                          display: 'flex', alignItems: 'center',
                          padding: '8px 10px 8px 14px', cursor: 'pointer',
                          background: isActive ? 'var(--bg-surface)' : 'transparent',
                          borderRadius: 6, margin: 4, gap: 8,
                        }}
                        onMouseOver={e => { if (!isActive) e.currentTarget.style.background = 'var(--bg-surface)'; }}
                        onMouseOut={e => { if (!isActive) e.currentTarget.style.background = isActive ? 'var(--bg-surface)' : 'transparent'; }}
                      >
                        <div
                          onClick={() => {
                            apiFetch(`/api/decks/${deckId}/versions/current`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ name: v.key }),
                            })
                              .then(async r => {
                                const d = await r.json().catch(() => ({ ok: false, error: 'Invalid server response' }));
                                if (!d.ok) {
                                  notifyError(d.error ? `Switch version failed: ${d.error}` : 'Switch version failed');
                                  return;
                                }
                                setCurrentVersion(d.data.current);
                                const match = assembledArtifacts.find(a => a.key.includes(`/${v.key}/`));
                                if (match) onSelectArtifact?.(match.key);
                                // Re-fetch the deck so signals.active_artifact_id
                                // (now keyed to current_version server-side)
                                // propagates → the generic-viewer preview src
                                // follows the switch instead of staying on the
                                // newest version.
                                onDeckChanged?.();
                              })
                              .catch(err => {
                                if (err instanceof AuthExpiredError) return;
                                notifyError(`Switch version failed: ${err?.message || 'network error'}`);
                              });
                            setSelectorOpen(false);
                          }}
                          style={{ flex: 1, minWidth: 0 }}
                        >
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                            <span style={{
                              fontSize: 'var(--fs-sm)', fontWeight: isActive ? 600 : 500,
                              color: 'var(--text-primary)', fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                            }}>
                              {v.key}
                            </span>
                            {subtitle && (
                              <span style={{
                                fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                              }}>
                                {subtitle}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)', marginTop: 2 }}>
                            {v.slides > 0 ? `${v.slides} slides · ` : ''}{formatRelativeTime(v.updated_at || v.created_at)}
                          </div>
                        </div>
                        <a
                          href={exportPptxUrl(deckId, v.key)}
                          download
                          title={`Download ${v.key} as .pptx`}
                          onClick={e => e.stopPropagation()}
                          style={{
                            color: 'var(--text-muted)',
                            display: 'inline-flex', alignItems: 'center',
                            padding: 6, borderRadius: 4, textDecoration: 'none',
                            flexShrink: 0,
                          }}
                          onMouseOver={e => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                          onMouseOut={e => { e.currentTarget.style.color = 'var(--text-muted)'; }}
                        >
                          <DownloadSimple size={13} weight="light" />
                        </a>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {editMode ? (
            <>
              {/* Save */}
              <button
                onClick={() => {
                  if (saving) return;
                  iframeRef.current?.contentWindow?.postMessage({ type: 'pp:save' }, '*');
                }}
                disabled={saving}
                style={{
                  ...btnStyle,
                  background: 'var(--ds-accent)',
                  borderColor: 'var(--ds-accent)',
                  color: 'var(--ds-on-accent)',
                  opacity: saving ? 0.5 : 1,
                  fontWeight: 600,
                }}
              >
                <FloppyDisk size={13} weight="bold" />
                <span style={{ fontSize: 'var(--fs-2xs)', marginLeft: 4 }}>
                  {saving ? 'Saving...' : 'Save'}
                </span>
              </button>
              {/* Cancel */}
              <button
                onClick={() => {
                  iframeRef.current?.contentWindow?.postMessage({ type: 'pp:exitEdit' }, '*');
                  setEditMode(false);
                }}
                style={{
                  ...btnStyle,
                  color: 'var(--text-secondary)',
                }}
              >
                <X size={13} weight="bold" />
                <span style={{ fontSize: 'var(--fs-2xs)', marginLeft: 4 }}>Cancel</span>
              </button>
            </>
          ) : (
            <>
              {/* Edit — HTML mode only. PPTX mode users edit in PowerPoint after download. */}
              {!readOnly && hasPreview && effectiveMode === 'html' && (
                <button
                  onClick={() => {
                    // Snapshot pre-edit slide text so we can diff after
                    // save and surface what actually changed in chat,
                    // at element granularity (heading vs bullet etc.).
                    try {
                      const doc = iframeRef.current?.contentDocument;
                      if (doc) editSnapshotRef.current = snapshotSlideElements(doc);
                    } catch {}
                    setEditMode(true);
                  }}
                  title="Edit slides"
                  style={btnStyle}
                >
                  <PencilSimple size={13} weight="light" />
                  <span>Edit</span>
                </button>
              )}
              {/* Comment — HTML mode only. Click-to-anchor: enables a
                  click capture inside the iframe; the next click opens
                  a popover anchored to that element so the user can
                  send a [user-comment] chat message with location +
                  intent baked in. */}
              {!readOnly && hasPreview && effectiveMode === 'html' && (
                <button
                  onClick={() => setCommentMode(v => !v)}
                  title={commentMode ? 'Cancel comment mode' : 'Click an element to comment on it'}
                  style={commentMode
                    ? { ...btnStyle, background: 'var(--ds-accent-soft)', borderColor: 'var(--ds-accent-soft)' }
                    : btnStyle}
                >
                  <ChatCircle size={13} weight="light" />
                  <span>{commentMode ? 'Cancel' : 'Comment'}</span>
                </button>
              )}
              {/* Export ▾ — downloads + share folded into one menu so the
                  tab-bar row stays compact (it was 6+ flat buttons that
                  overlapped the version selector / tab title at narrow
                  widths). */}
              {(hasPreview || currentVersion) && (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => { setExportOpen(o => !o); setMoreOpen(false); }}
                    title="Export & share"
                    style={exportOpen ? { ...btnStyle, background: 'var(--bg-surface)' } : btnStyle}
                  >
                    <DownloadSimple size={13} weight="light" />
                    <span>Export ▾</span>
                  </button>
                  {exportOpen && (
                    <div style={menuStyle}>
                      {/* Browser PPTX export (ported from pitchpilot-cf):
                          editable DOM → dom-to-pptx, screenshot fallback. */}
                      {hasPreview && (
                        <button
                          disabled={exportBusy}
                          onClick={() => { void handleDomPptxExport(); }}
                          title="Download PPTX (browser export)"
                          style={{ ...menuItemStyle, opacity: exportBusy ? 0.5 : 1 }}
                        >
                          <DownloadSimple size={14} weight="light" />
                          <span>{pptxExporting ? 'Exporting PPTX…' : 'Download PPTX'}</span>
                        </button>
                      )}
                      {hasPreview && (
                        <button
                          disabled={exportBusy}
                          onClick={() => { void handleDomPdfExport(); }}
                          title="Download PDF (browser render)"
                          style={{ ...menuItemStyle, opacity: exportBusy ? 0.5 : 1 }}
                        >
                          <DownloadSimple size={14} weight="light" />
                          <span>{pdfExporting ? 'Exporting PDF…' : 'Download PDF'}</span>
                        </button>
                      )}
                      {/* Download HTML — client-side dump of the iframe DOM. */}
                      {hasPreview && effectiveMode === 'html' && (
                        <button
                          disabled={downloading}
                          onClick={() => {
                            if (downloading) return;
                            setDownloading(true);
                            (async () => {
                            try {
                              // Issue #11: dumping the nested preview iframe
                              // produced nothing on some engines — fetch the
                              // assembled export server-side instead.
                              const res = await fetch(`/api/decks/${deckId}/preview`, { credentials: 'include' });
                              if (!res.ok) {
                                notifyError(`Download failed (${res.status})`);
                                return;
                              }
                              const html = await res.text();
                              const blob = new Blob([html], { type: 'text/html; charset=utf-8' });
                              const title = (deckTitle || 'presentation').replace(/[^a-zA-Z0-9._-]/g, '_');
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `${title}.html`;
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                              setTimeout(() => URL.revokeObjectURL(url), 0);
                            } catch (err) {
                              notifyError(`Download failed: ${(err as Error)?.message || 'unknown error'}`);
                            } finally {
                              setDownloading(false);
                              setExportOpen(false);
                            }
                            })();
                          }}
                          title="Download HTML"
                          style={{ ...menuItemStyle, opacity: downloading ? 0.5 : 1 }}
                        >
                          <DownloadSimple size={14} weight="light" />
                          <span>{downloading ? 'Downloading…' : 'Download HTML'}</span>
                        </button>
                      )}
                      {/* Per-artifact download — assembled HTML, or .pptx
                          when the active artifact is a PPTX deck-group. */}
                      {activeArtifactSignal && (hasPreview || currentVersion) && (() => {
                        const isPptx = activeArtifactSignal.mime ===
                          'application/vnd.openxmlformats-officedocument.presentationml.presentation';
                        return (
                          <a
                            href={activeArtifactSignal.download_url}
                            download
                            onClick={() => setExportOpen(false)}
                            title={`Download ${activeArtifactSignal.display_name} as .${isPptx ? 'pptx' : 'html'}`}
                            style={menuItemStyle}
                          >
                            <DownloadSimple size={14} weight="light" />
                            <span>{isPptx ? 'Download PPTX' : 'Download HTML (assembled)'}</span>
                          </a>
                        );
                      })()}
                      {/* PDF Export */}
                      {hasPreview && (
                        <button
                          onClick={() => {
                            setExportOpen(false);
                            const printWin = window.open(`/api/decks/${deckId}/preview`, '_blank');
                            if (printWin) {
                              printWin.addEventListener('load', () => {
                                const doc = printWin.document;
                                const ppDeck = doc.getElementById('pp-deck');
                                const ps = doc.createElement('style');
                                ps.textContent = ppDeck
                                  ? `@media print { @page { size: landscape; margin: 0; } *, *::before, *::after { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } html, body { margin: 0 !important; height: auto !important; overflow: visible !important; } #pp-deck { display: block !important; overflow: visible !important; width: auto !important; height: auto !important; } #pp-deck .slide { width: 100vw !important; height: 100vh !important; page-break-after: always !important; break-after: page !important; overflow: hidden !important; box-sizing: border-box !important; } #pp-deck .slide:last-child { page-break-after: auto !important; } }`
                                  : `@media print { *, *::before, *::after { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; } html, body { height: auto !important; overflow: visible !important; } }`;
                                doc.head.appendChild(ps);
                                setTimeout(() => printWin.print(), 300);
                              });
                            }
                          }}
                          title="Export PDF"
                          style={menuItemStyle}
                        >
                          <FilePdf size={14} weight="light" />
                          <span>Export PDF</span>
                        </button>
                      )}
                      {/* Share */}
                      {!readOnly && <button onClick={() => { setExportOpen(false); setShowShare(true); }} title="Share" style={menuItemStyle}>
                        <ShareNetwork size={14} weight="light" />
                        <span>Share link</span>
                      </button>}
                    </div>
                  )}
                </div>
              )}
              {/* More ⋯ — deck utilities (import reference material, brand
                  profile, members) folded out of the main row. */}
              {!readOnly && <div style={{ position: 'relative' }}>
                <button
                  onClick={() => { setMoreOpen(o => !o); setExportOpen(false); }}
                  title="More"
                  style={moreOpen ? { ...btnStyle, background: 'var(--bg-surface)' } : btnStyle}
                >
                  <span style={{ fontSize: 'var(--fs-md)', lineHeight: 1, letterSpacing: 1 }}>⋯</span>
                </button>
                {moreOpen && (
                  <div style={menuStyle}>
                    {/* Import — fold pdf/pptx/docx/url into the deck's context. */}
                    <button onClick={() => { setMoreOpen(false); setShowImport(true); }} title="Import a PDF/PPTX/DOCX/URL as reference material for the agent" style={menuItemStyle}>
                      <UploadSimple size={14} weight="light" />
                      <span>Import reference…</span>
                    </button>
                    {/* Brand — capture / regenerate the deck's brand profile. */}
                    <button onClick={() => { setMoreOpen(false); setShowBrand(true); }} title="Brand profile" style={menuItemStyle}>
                      <Palette size={14} weight="light" />
                      <span>Brand profile</span>
                    </button>
                    {/* Members — invite / role / transfer. */}
                    <button onClick={() => { setMoreOpen(false); setShowMembers(true); }} title="Deck members" style={menuItemStyle}>
                      <Users size={14} weight="light" />
                      <span>Members</span>
                    </button>
                  </div>
                )}
              </div>}
              {/* Fullscreen opens a clean presenter surface instead of
                  enlarging the embedded workspace iframe. */}
              <button
                onClick={handleFullscreen}
                title="Present fullscreen"
                style={btnStyle}
              >
                <ArrowsOut size={13} weight="light" />
              </button>
            </>
          )}
        </div>

      </div>

      {/* Close dropdown on outside click — must be below dropdown z-index */}
      {selectorOpen && (
        <div
          onClick={() => setSelectorOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 4 }}
        />
      )}
      {(exportOpen || moreOpen) && (
        <div
          onClick={() => { setExportOpen(false); setMoreOpen(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 49 }}
        />
      )}

      <div style={{ flex: 1, minHeight:0, position: 'relative' }} aria-busy={frameState==='loading'}>
        {frameState !== 'ready' && <div style={{position:'absolute',inset:0,zIndex:1,display:'flex',background:'var(--ds-canvas)'}}>
          <PanelState kind={frameState==='error'?'error':'loading'} title={frameState==='error'?"Couldn't open your presentation":'Loading your presentation'} description={frameState==='error'?'The slide content did not load. Try opening it again, or check the saved files in Artifacts.':'Opening the saved slides for this project.'} action={frameState==='error'?'Retry presentation':undefined} onAction={() => setFrameRetry(r=>r+1)} />
        </div>}
        {exportProgress && <PreviewExportOverlay progress={exportProgress} />}
        <iframe
          ref={iframeRef}
          key={`${previewRefreshKey}-${effectivePreviewKey}-${editMode ? 'edit' : 'view'}-${frameRetry}`}
          onLoad={onFrameLoaded} onError={() => setFrameState('error')}
          src={previewUrl}
          title="Deck Preview"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', background: 'var(--bg-surface)' }}
        />
      </div>
      {/* Speaker-notes pane (PP-main parity gap C). Show under the slide
          viewer when not editing, the flag is on, and the active artifact
          is a slide deck-group (HTML or PPTX-SVG — both write .note.md).
          With no signals (legacy decks) the iframe still shows the deck,
          so fall back to showing the pane. */}
      {!editMode && notesPaneEnabled &&
        (!activeArtifactSignal || activeArtifactSignal.kind === 'deck-group') && (
        <SpeakerNotesPane deckId={deckId} slideNum={currentSlideNum} />
      )}
      {showShare && <ShareDialog deckId={deckId} onClose={() => setShowShare(false)} />}
      {showImport && deckId && (
        <ImportDialog
          deckId={deckId}
          onClose={() => setShowImport(false)}
          onImported={() => {
            // Trigger a context refresh / surface a synthetic chat message
            // by dispatching a custom event the parent listens for. The
            // App-level listener already exists for edit-saved.
            window.dispatchEvent(
              new CustomEvent('pp:talent-imported', { detail: { deckId } }),
            );
          }}
        />
      )}
      {showBrand && deckId && (
        <BrandDialog
          deckId={deckId}
          onClose={() => setShowBrand(false)}
          onApplied={(profile) => {
            // Persist brand profile into the deck's memory section so
            // composePrompt picks it up on the next turn. Best-effort:
            // failures are logged but don't block the dialog.
            const blob = new Blob(
              [JSON.stringify(profile, null, 2)],
              { type: 'application/json' },
            );
            const file = new File([blob], 'brand_profile.json', {
              type: 'application/json',
            });
            uploadContextFile(deckId, file, 'memory').catch((e) =>
              console.warn('brand profile persist failed', e),
            );
          }}
        />
      )}
      {showMembers && deckId && (() => {
        let currentUserId: string | null = null;
        try {
          currentUserId = JSON.parse(localStorage.getItem('pp_user') || 'null')?.id ?? null;
        } catch {
          currentUserId = null;
        }
        return (
          <MembersDialog
            deckId={deckId}
            workspaceId={workspaceId ?? null}
            currentUserId={currentUserId}
            onClose={() => setShowMembers(false)}
          />
        );
      })()}
      {/* BuildModePicker removed — Stage 4 of #87 alignment. */}
      {/* Anchor-comment popover. Position is fixed in main-window
          coordinates so it stays glued to the clicked iframe element
          even with the chat sidebar; stops propagation so an outside
          click in the iframe doesn't re-trigger the click handler. */}
      {commentPopover && (
        <div
          style={{
            position: 'fixed', left: commentPopover.x, top: commentPopover.y,
            zIndex: 1000, minWidth: 280, maxWidth: 360,
            background: 'var(--bg-surface)', border: '1px solid var(--border-strong)',
            borderRadius: 10, boxShadow: '0 8px 32px var(--ds-shadow-color)',
            padding: 12, display: 'flex', flexDirection: 'column', gap: 8,
            fontSize: 'var(--fs-sm)', color: 'var(--text-primary)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)', letterSpacing: '0.02em' }}>
            {commentPopover.anchor}
          </div>
          {commentPopover.elementText && (
            <div style={{
              fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)',
              fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis',
              whiteSpace: 'nowrap', maxWidth: '100%',
            }} title={commentPopover.elementText}>
              "{commentPopover.elementText}"
            </div>
          )}
          <textarea
            ref={commentTextRef}
            placeholder="What would you like to change?"
            style={{
              width: '100%', minHeight: 64, resize: 'vertical', boxSizing: 'border-box',
              border: '1px solid var(--border)', borderRadius: 6, padding: 8,
              fontFamily: 'inherit', fontSize: 'var(--fs-sm)', background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendComment(); }
              if (e.key === 'Escape') { e.preventDefault(); closeCommentPopover(); }
            }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              onClick={closeCommentPopover}
              style={{
                padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)',
                background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 'var(--fs-sm)',
              }}
            >Cancel</button>
            <button
              onClick={sendComment}
              style={{
                padding: '4px 12px', borderRadius: 6, border: 'none',
                background: 'var(--brand)', color: 'var(--ds-on-accent)', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 'var(--fs-sm)', fontWeight: 500,
              }}
            >Send</button>
          </div>
        </div>
      )}
      <style>{`
        .tab-scroll::-webkit-scrollbar { display: none; }
        @container (max-width: 500px) {
          .pp-tab-actions button span { display: none; }
        }
        @media (max-width: 900px) {
          .pp-tab-actions button span { display: none !important; }
          .pp-tab-actions button { padding: 4px 6px !important; }
        }
      `}</style>
    </div>
  );
}

/**
 * Right-panel overlay for non-deck artifact previews. Sits on top of the
 * PreviewPanel content so the underlying deck iframe stays mounted, then
 * branches by mime type:
 *   - image/* and application/pdf  → inline iframe via artifact proxy
 *   - everything else (incl. pptx) → metadata card with Download button
 */
function PreviewExportOverlay({ progress }: { progress: ExportProgressState }) {
  const pct = Math.max(0, Math.min(100, Math.round(progress.progress)));
  const kindLabel = progress.kind.toUpperCase();
  const isError = progress.status === 'error';
  const barColor = isError ? 'var(--ds-danger)' : 'var(--brand, #2684ff)';
  const statusLabel = progress.status === 'complete'
    ? 'Ready'
    : progress.status === 'error'
      ? 'Failed'
      : `${pct}%`;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`${kindLabel} export ${progress.label}`}
      style={{
        position: 'absolute', inset: 0, zIndex: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, background: 'var(--ds-shadow-color)', pointerEvents: 'auto',
      }}
    >
      <div style={{
        width: 'min(360px, 82%)', background: 'var(--bg-primary)',
        border: '1px solid var(--border)', borderRadius: 8,
        padding: '14px 16px 15px', boxShadow: '0 10px 32px var(--ds-shadow-color)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, fontSize: 12 }}>
          <strong style={{ flexShrink: 0 }}>{kindLabel}</strong>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.75 }}>{progress.label}</span>
          <span style={{ flexShrink: 0, color: isError ? 'var(--ds-danger)' : 'var(--text-muted)' }}>{statusLabel}</span>
        </div>
        <div style={{ height: 4, marginTop: 11, borderRadius: 999, background: 'var(--bg-surface)', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: barColor, transition: 'width 180ms ease' }} />
        </div>
      </div>
    </div>
  );
}

function ArtifactOverlay({
  deckId,
  artifact,
  onClose,
}: {
  deckId: string;
  artifact: { fileHash: string; filename: string; fileMime?: string; fileSize?: number };
  onClose: () => void;
}) {
  const mime = artifact.fileMime || '';
  const inlineRenderable = mime.startsWith('image/') || mime === 'application/pdf';
  const proxyBase = `/api/decks/${deckId}/artifacts/${encodeURIComponent(artifact.fileHash)}`;
  const inlineUrl = `${proxyBase}${artifact.fileMime ? `?mime=${encodeURIComponent(artifact.fileMime)}` : ''}`;
  const downloadUrl = `${proxyBase}?download=1${artifact.fileMime ? `&mime=${encodeURIComponent(artifact.fileMime)}` : ''}`;
  return (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 30,
      background: 'var(--bg-primary)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 16px', borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}>
        <span style={{
          fontSize: 'var(--fs-base)', color: 'var(--text-primary)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{artifact.filename}</span>
        <button
          onClick={onClose}
          style={{
            background: 'transparent', border: '1px solid var(--border)',
            borderRadius: 4, padding: '4px 10px',
            fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >Back to deck</button>
      </div>
      {inlineRenderable ? (
        <iframe
          src={inlineUrl}
          title={artifact.filename}
          style={{ flex: 1, border: 'none', minHeight: 0, background: 'var(--bg-surface)' }}
        />
      ) : (
        <div style={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
        }}>
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
            padding: 32, minWidth: 280, maxWidth: 480,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)', borderRadius: 8,
          }}>
            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 500, color: 'var(--text-primary)', textAlign: 'center', wordBreak: 'break-all' }}>
              {artifact.filename}
            </div>
            {artifact.fileSize !== undefined && (
              <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                {formatFileSize(artifact.fileSize)}
              </div>
            )}
            <a
              href={downloadUrl}
              download={artifact.filename}
              style={{
                background: 'var(--brand)',
                color: 'var(--ds-on-accent)', textDecoration: 'none',
                padding: '8px 24px', borderRadius: 4,
                fontSize: 'var(--fs-base)',
              }}
            >Download</a>
          </div>
        </div>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
